/* eslint-disable max-lines */
import { useState } from 'react';
import { toast } from 'react-toastify';
import StepShell from './StepShell';
import { colors, layout } from './styles';
import { BaseStepProps } from './steps/BaseStepProps';
import { DocStep } from './types';

const statusBadge = (status: DocStep['status']) => {
  const colorMap = {
    completed: { bg: '#D1FAE5', fg: colors.success, icon: 'fa-check' },
    partial: { bg: '#FEF3C7', fg: '#92400E', icon: 'fa-ellipsis-h' },
    pending: { bg: '#FEE2E2', fg: colors.danger, icon: 'fa-exclamation' },
  };
  const cfg = colorMap[status];
  return (
    <div
      style={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: cfg.bg,
        color: cfg.fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      <i className={`fas ${cfg.icon}`} />
    </div>
  );
};

const statusLabel: Record<DocStep['status'], string> = {
  completed: 'Completed',
  partial: 'In progress',
  pending: 'Not started',
};

const ReviewStep = ({
  steps,
  stepIndex,
  totalSteps,
  completedSteps,
  onBack,
  onPrevious,
  onJumpTo,
  onComplete,
}: BaseStepProps) => {
  const [certified, setCertified] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reviewableSteps = steps.filter((s) => s.kind !== 'REVIEW');
  const allCompleted = reviewableSteps.every((s) => s.status === 'completed');

  const canSubmit = certified && allCompleted && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      toast.success('Paperwork submitted successfully!');
      onComplete();
    } catch (err: any) {
      console.error(err);
      toast.error('Could not finalize submission.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StepShell
      stepNumber={stepIndex + 1}
      totalSteps={totalSteps}
      completedSteps={completedSteps}
      title="Review & Submit"
      onBack={onBack}
      onPrevious={onPrevious}
      onNext={handleSubmit}
      nextLabel={submitting ? 'Submitting...' : 'Submit Paperwork'}
      nextDisabled={!canSubmit}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Review &amp; Submit</h3>
      <p style={{ color: colors.muted, fontSize: 14, marginTop: 6, marginBottom: 16 }}>
        Please review every section before submitting. You can go back and edit any step that is not yet complete.
      </p>

      {!allCompleted && (
        <div style={{ ...layout.infoBox, background: '#FEF3C7', color: '#92400E' }}>
          <i className="fas fa-exclamation-triangle" />
          <span>Some steps are not yet complete. Click on any item below to finish it before submitting.</span>
        </div>
      )}

      {reviewableSteps.map((s) => (
        <div
          key={s.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: 14,
            background: '#F9FAFB',
            border: `1px solid ${colors.border}`,
            borderRadius: 10,
            marginBottom: 10,
          }}
        >
          {statusBadge(s.status)}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{s.title}</div>
            <div style={{ fontSize: 12, color: colors.muted }}>{statusLabel[s.status]}</div>
          </div>
          <button
            type="button"
            onClick={() => onJumpTo(s.id)}
            style={{
              background: 'transparent',
              border: 'none',
              color: colors.accent,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {s.status === 'completed' ? 'Review' : 'Continue'}
          </button>
        </div>
      ))}

      <hr style={layout.divider} />

      <p style={{ color: colors.muted, fontSize: 13, marginTop: 0, marginBottom: 12 }}>
        After you submit, your filled and signed documents will be available in your employee profile under Files.
      </p>

      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: 14,
          background: '#F9FAFB',
          border: `1px solid ${colors.border}`,
          borderRadius: 10,
          cursor: 'pointer',
          fontSize: 13,
          lineHeight: 1.5,
          marginBottom: 16,
        }}
      >
        <input
          type="checkbox"
          checked={certified}
          onChange={(e) => setCertified(e.target.checked)}
          style={{ marginTop: 2 }}
        />
        <span>
          I certify that the information provided in this application is true and complete to the best of my knowledge.
          I understand that any false information or omission may disqualify me from further consideration for
          employment and may result in my dismissal if discovered at a later date. I authorize the verification of any
          or all information listed above.
        </span>
      </label>

      <div
        style={{
          background: colors.accentSoft,
          border: `1px solid ${colors.accent}`,
          borderRadius: 10,
          padding: 14,
          marginBottom: 8,
        }}
      >
        <div style={{ fontWeight: 700, color: '#1E3A8A', fontSize: 14, marginBottom: 4 }}>Electronic Signature</div>
        <div style={{ color: '#1E3A8A', fontSize: 13, lineHeight: 1.5 }}>
          By clicking &quot;Submit Paperwork&quot; below, I acknowledge that the signatures provided in each step
          constitute my electronic signatures and have the same legal effect as handwritten signatures.
        </div>
      </div>
    </StepShell>
  );
};

export default ReviewStep;
